import { notify } from "@general-backoffice/core";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import {
  type TaxReport,
  type TaxReportQuarter,
  TaxReportQuarterSchema,
  taxReportsEndpoints,
  type TaxReportType,
  TaxReportTypeSchema,
} from "../../../../api";
import getErrorMessage from "../../../../utils/getErrorMessage";

interface ReportTypeConfig {
  type: string;
  name: string;
  frequency: string;
}

const REPORT_TYPES: ReportTypeConfig[] = [
  { type: "MODELO_303", name: "Modelo 303", frequency: "quarterly" },
  { type: "MODELO_349", name: "Modelo 349", frequency: "both" },
  { type: "MODELO_111", name: "Modelo 111", frequency: "quarterly" },
  { type: "MODELO_190", name: "Modelo 190", frequency: "annual" },
];

const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

const TaxReports: React.FC = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "adminPages.taxReports" });

  // State
  const [reports, setReports] = useState<TaxReport[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedType, setSelectedType] = useState<TaxReportType>("MODELO_303");
  const [selectedQuarter, setSelectedQuarter] = useState<TaxReportQuarter>("Q1");

  // Get available years (from 2020 to current + 1)
  const currentYear = new Date().getFullYear();
  const availableYears = Array.from({ length: currentYear - 2020 + 2 }, (_, i) => 2020 + i);

  // Load reports for selected year
  const loadReports = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // getReportsByYear returns { data, meta } - we extract data
      const { data: reportsData } = await taxReportsEndpoints.getReportsByYear(selectedYear);
      setReports(reportsData || []);
    } catch (err: unknown) {
      console.error("Failed to load reports:", err);
      setError(getErrorMessage(err, t("loadError")));
    } finally {
      setIsLoading(false);
    }
  }, [selectedYear, t]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  // Generate report
  const handleGenerate = async () => {
    const reportType = REPORT_TYPES.find((r) => r.type === selectedType);

    // Validate quarter for report type
    if (reportType?.frequency === "annual" && selectedQuarter !== "ANNUAL") {
      notify.error(t("annualOnlyError"));
      return;
    }
    if (reportType?.frequency === "quarterly" && selectedQuarter === "ANNUAL") {
      notify.error(t("quarterlyOnlyError"));
      return;
    }

    setIsGenerating(true);
    try {
      await taxReportsEndpoints.generate({
        type: selectedType,
        year: selectedYear,
        quarter: selectedQuarter,
      });
      notify.success(t("generateSuccess"));
      loadReports();
    } catch (err: unknown) {
      console.error("Failed to generate report:", err);
      notify.error(getErrorMessage(err, t("generateError")));
    } finally {
      setIsGenerating(false);
    }
  };

  // Export report
  const handleExport = async (report: TaxReport) => {
    try {
      // exportCSV returns Blob directly, not wrapped in { data, meta }
      const data = await taxReportsEndpoints.exportCSV(report.id);
      const blob = new Blob([data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${report.type}_${report.year}_${report.quarter}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      notify.success(t("exportSuccess"));
    } catch (err: unknown) {
      console.error("Failed to export report:", err);
      notify.error(getErrorMessage(err, t("exportError")));
    }
  };

  // Delete report
  const handleDelete = async (report: TaxReport) => {
    if (!window.confirm(t("deleteConfirm"))) return;

    try {
      await taxReportsEndpoints.deleteReport(report.id);
      notify.success(t("deleteSuccess"));
      loadReports();
    } catch (err: unknown) {
      console.error("Failed to delete report:", err);
      notify.error(getErrorMessage(err, t("deleteError")));
    }
  };

  // Get report type config
  const getReportTypeConfig = (type: string): ReportTypeConfig | undefined => REPORT_TYPES.find((r) => r.type === type);

  // Get available quarters based on report type
  const getAvailableQuarters = (): string[] => {
    const reportType = getReportTypeConfig(selectedType);
    if (reportType?.frequency === "annual") return ["ANNUAL"];
    if (reportType?.frequency === "quarterly") return QUARTERS;
    return [...QUARTERS, "ANNUAL"];
  };

  // Format amount
  const formatAmount = (amount: number | undefined | null): string => {
    if (amount == null) return "-";
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  // Get status badge
  const getStatusBadge = (status: string | undefined) => {
    const colors: Record<string, string> = {
      generated: "success",
      pending: "warning",
      error: "danger",
    };
    return <Badge color={colors[status || ""] || "secondary"}>{status}</Badge>;
  };

  return (
    <Container className="mt-4" fluid>
      {/* Generate Report Card */}
      <Row className="mb-4">
        <Col>
          <Card className="shadow">
            <CardHeader className="bg-gradient-dark text-white">
              <h5 className="mb-0">
                <i className="fas fa-file-invoice mr-2"></i>
                {t("generateTitle")}
              </h5>
            </CardHeader>
            <CardBody>
              <Row>
                <Col md={3}>
                  <FormGroup>
                    <Label for="tax-reports-field-1">{t("reportType")}</Label>
                    <Input id="tax-reports-field-1"
                      type="select"
                      value={selectedType}
                      onChange={(e) => {
                        const value = TaxReportTypeSchema.parse(e.target.value);
                        setSelectedType(value);
                        // Reset quarter when changing type
                        const config = getReportTypeConfig(value);
                        if (config?.frequency === "annual") {
                          setSelectedQuarter("ANNUAL");
                        } else if (selectedQuarter === "ANNUAL") {
                          setSelectedQuarter("Q1");
                        }
                      }}
                    >
                      {REPORT_TYPES.map((r) => (
                        <option key={r.type} value={r.type}>
                          {r.name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label for="tax-reports-field-2">{t("year")}</Label>
                    <Input id="tax-reports-field-2" type="select" value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
                      {availableYears.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label for="tax-reports-field-3">{t("period")}</Label>
                    <Input id="tax-reports-field-3"
                      type="select"
                      value={selectedQuarter}
                      onChange={(e) => setSelectedQuarter(TaxReportQuarterSchema.parse(e.target.value))}
                    >
                      {getAvailableQuarters().map((q) => (
                        <option key={q} value={q}>
                          {q === "ANNUAL" ? t("annual") : q}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <Button color="primary" onClick={handleGenerate} disabled={isGenerating} className="mb-3">
                    {isGenerating ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        {t("generating")}
                      </>
                    ) : (
                      <>
                        <i className="fas fa-cogs mr-2"></i>
                        {t("generate")}
                      </>
                    )}
                  </Button>
                </Col>
              </Row>
              <Row>
                <Col>
                  <small className="text-muted">
                    <strong>Modelo 303:</strong> {t("modelo303Desc")} | <strong>Modelo 349:</strong>{" "}
                    {t("modelo349Desc")} | <strong>Modelo 111:</strong> {t("modelo111Desc")} |{" "}
                    <strong>Modelo 190:</strong> {t("modelo190Desc")}
                  </small>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Reports List */}
      <Row>
        <Col>
          <Card className="shadow">
            <CardHeader className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-list mr-2"></i>
                {t("reportsFor")} {selectedYear}
              </h5>
              <Button color="link" onClick={loadReports} disabled={isLoading}>
                <i className={`fas fa-sync ${isLoading ? "fa-spin" : ""}`}></i>
              </Button>
            </CardHeader>
            <CardBody>
              {error && <Alert color="danger">{error}</Alert>}

              {isLoading ? (
                <div className="text-center py-4">
                  <Spinner color="primary" />
                </div>
              ) : reports.length === 0 ? (
                <Alert color="info" className="mb-0">
                  {t("noReports")}
                </Alert>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>{t("type")}</th>
                      <th>{t("period")}</th>
                      <th>{t("status")}</th>
                      <th>{t("generatedAt")}</th>
                      <th className="text-right">{t("baseAmount")}</th>
                      <th className="text-right">{t("vatAmount")}</th>
                      <th className="text-right">{t("irpfAmount")}</th>
                      <th className="text-center">{t("transactions")}</th>
                      <th>{t("actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map((report) => (
                      <tr key={report.id}>
                        <td>
                          <strong>{report.type?.replace("_", " ")}</strong>
                        </td>
                        <td>{report.quarter === "ANNUAL" ? t("annual") : report.quarter}</td>
                        <td>{getStatusBadge(report.status)}</td>
                        <td>{report.generatedAt ? new Date(report.generatedAt).toLocaleDateString("es-ES") : "-"}</td>
                        <td className="text-right">{formatAmount(report.totalBaseAmount)}</td>
                        <td className="text-right">{formatAmount(report.totalVATAmount)}</td>
                        <td className="text-right">{formatAmount(report.totalIRPFAmount)}</td>
                        <td className="text-center">{report.transactionCount ?? "-"}</td>
                        <td>
                          <Button
                            color="info"
                            size="sm"
                            className="mr-1"
                            onClick={() => handleExport(report)}
                            title={t("exportCSV")}
                          >
                            <i className="fas fa-download"></i>
                          </Button>
                          <Button color="danger" size="sm" onClick={() => handleDelete(report)} title={t("delete")}>
                            <i className="fas fa-trash"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default TaxReports;
